/**
 * Enhanced Signal Detection Utility
 * 
 * This module provides standardized functions for detecting trading signals
 * including crossovers, price comparisons, and other technical indicators.
 * It implements stateful detection to prevent duplicate signals.
 */

import { 
  normalizeIndicatorData, 
  extractValues, 
  extractTimestamps,
  isValidTimestamp,
  extractValidTimestamps
} from './DataNormalizer';
import { extractThreshold } from './ThresholdExtractor';
import { SignalTracker, defaultTracker } from './SignalTracker';
import IndicatorAdapterFactory from './IndicatorAdapter';

/**
 * Detects trading signals in time series data based on various operators
 * 
 * @param {Object|Array} params - Parameter object for signal detection, or first argument of legacy function call
 * @param {Array} params.data - Array of data points to analyze
 * @param {string} params.operator - Comparison operator: <, >, =, and, or, crossover_above, crossover_below
 * @param {number|string|Object} params.threshold - Value to compare against
 * @param {boolean} [params.findAll=false] - Whether to find all matches (true) or just the first one (false)
 * @param {string} [params.indicatorType='generic'] - Type of indicator (rsi, macd, etc.)
 * @param {boolean} [params.debug=false] - Enable debug logging
 * @param {Object} [params.tracker] - Optional SignalTracker instance (uses default if not provided)
 * @param {string} [params.dataSource='taapi'] - Source of the data (taapi, alpha_vantage, yahoo_finance)
 * @returns {Array|number|null} - Array of matches, single index, or null if no match
 */
const detectSignals = function(params) {
  // Support legacy function signature while also accepting parameter object
  const args = Array.from(arguments);
  if (args.length > 1 || !params || typeof params !== 'object') {
    console.log('[Signal Detection] Legacy function call detected, converting to parameter object');
    const [data, operator, threshold, findAll = false, options = {}] = args;
    return detectSignals({
      data,
      operator,
      threshold,
      findAll,
      ...options
    });
  }
  
  // Destructure parameters with defaults
  const {
    data,
    operator,
    threshold: rawThreshold,
    findAll = false,
    indicatorType = 'generic',
    debug = false,
    tracker = defaultTracker,
    dataSource = 'taapi'
  } = params;
  
  console.log('==================== SIGNAL DETECTION STARTING ====================');
  console.log(`[Signal Detection] Detecting signals with operator ${operator} and threshold ${rawThreshold}`);
  
  if (debug) {
    console.log(`[Signal Detection] Call stack: ${new Error().stack}`);
  }
  
  // Normalize the threshold
  const threshold = extractThreshold(rawThreshold);
  
  // Handle empty or invalid data
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.log(`[Signal Detection] No data points provided or invalid data format`);
    return null;
  }
  
  if (debug) {
    console.log(`[Signal Detection] Starting detection with operator: ${operator}, threshold: ${threshold}, findAll: ${findAll}, data points: ${data.length}`);
    console.log(`[Signal Detection] First item:`, JSON.stringify(data[0]));
  }
  
  // Use IndicatorAdapter to standardize the input data format based on data source
  const normalizedData = IndicatorAdapterFactory.process(data, dataSource, { debug });
  if (normalizedData.length === 0) {
    console.log(`[Signal Detection] No valid data points after normalization`);
    return null;
  }
  
  if (debug) {
    console.log(`[Signal Detection] Normalized data into ${normalizedData.length} data points`);
    console.log(`[Signal Detection] First normalized item:`, JSON.stringify(normalizedData[0]));
    if (normalizedData.length > 1) {
      console.log(`[Signal Detection] Second normalized item:`, JSON.stringify(normalizedData[1]));
    }
  }
  
  // Extract values and timestamps from normalized data
  const values = extractValues(normalizedData);
  const timestamps = extractTimestamps(normalizedData);
  
  if (values.length === 0) {
    console.log(`[Signal Detection] No valid values found after processing data`);
    return null;
  }
  
  // Initialize prevValue based on first-time activation or previous runs
  let prevValue = tracker.getLastValue(indicatorType, threshold, operator);
  
  if (prevValue === null) {
    // First-time activation, initialize with the earliest value in our dataset
    prevValue = values[0];
    console.log(`[Signal Detection] First-time activation, initializing with value: ${prevValue}`);
    tracker.trackValue(indicatorType, threshold, operator, prevValue, timestamps[0]);
  } else {
    console.log(`[Signal Detection] Using previously stored value: ${prevValue}`);
  }
  
  const matches = [];
  const thresholdValue = parseFloat(threshold);
  if (isNaN(thresholdValue)) {
    console.log(`[Signal Detection] Invalid threshold value: ${threshold}`);
    return findAll ? [] : null;
  }
  
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    const timestamp = timestamps[i];
    
    let match = false;
    
    // Standard comparison operators
    if (operator === "<" && value < thresholdValue) {
      if (debug) console.log(`[Signal Detection] Found < match at index ${i}: ${value} < ${thresholdValue}`);
      match = true;
    } else if (operator === ">" && value > thresholdValue) {
      if (debug) console.log(`[Signal Detection] Found > match at index ${i}: ${value} > ${thresholdValue}`);
      match = true;
    } else if (operator === "=" && value === thresholdValue) {
      if (debug) console.log(`[Signal Detection] Found = match at index ${i}: ${value} = ${thresholdValue}`);
      match = true;
    } else if (operator === "and" && value && thresholdValue) {
      match = true;
    } else if (operator === "or" && (value || thresholdValue)) {
      match = true;
    } 
    // Crossover detection - requires previous value
    else if (operator === "crossover_above" && prevValue !== null) {
      // Fixed crossover detection: Check that previous value was BELOW and current is AT OR ABOVE the threshold
      if (prevValue < thresholdValue && value >= thresholdValue) {
        match = true;
        if (debug) {
          console.log(`[Signal Detection] Crossover above detected at index ${i}: ${prevValue} → ${value}, threshold: ${thresholdValue}`);
        }
        
        // Store timestamp of this signal to avoid duplicate detection
        tracker.trackValue(indicatorType, threshold, operator, value, timestamp);
        console.log(`[Signal Detection] Stored signal time: ${new Date(timestamp).toISOString()}`);
      }
    } else if (operator === "crossover_below" && prevValue !== null) {
      // Fixed crossover detection: Check that previous value was ABOVE and current is AT OR BELOW the threshold
      if (prevValue > thresholdValue && value <= thresholdValue) {
        match = true;
        if (debug) {
          console.log(`[Signal Detection] Crossover below detected at index ${i}: ${prevValue} → ${value}, threshold: ${thresholdValue}`);
        }
        
        // Store timestamp of this signal to avoid duplicate detection
        tracker.trackValue(indicatorType, threshold, operator, value, timestamp);
        console.log(`[Signal Detection] Stored signal time: ${new Date(timestamp).toISOString()}`);
      }
    }
    
    if (match) {
      if (debug) console.log(`[Signal Detection] Match found at index ${i}, value: ${value}, threshold: ${thresholdValue}`);
      if (findAll) {
        matches.push({
          index: i,
          item: normalizedData[i],
          value: value,
          timestamp: timestamp
        });
      } else {
        // Always update the last value for this indicator before returning
        tracker.trackValue(indicatorType, threshold, operator, value, timestamp);
        return i; // Return the first match index
      }
    }
    
    // Update prevValue for next iteration - critical for crossover detection
    prevValue = value;
  }
  
  // Always update the last value for this indicator before returning
  if (values.length > 0) {
    const latestValue = values[values.length - 1];
    tracker.trackValue(indicatorType, threshold, operator, latestValue, timestamps[timestamps.length - 1]);
  }
  
  console.log(`[Signal Detection] ${matches.length > 0 ? `Found ${matches.length} matches` : 'No matches found'}`);
  
  return findAll ? matches : null;
};

/**
 * Filter data points by timestamp
 * 
 * @param {Array} data - Array of data points
 * @param {number} timestamp - Timestamp to filter by
 * @param {boolean} beforeTimestamp - Whether to get data points before or after the timestamp
 * @returns {Array} - Filtered data points
 */
export const filterDataByTimestamp = (data, timestamp, beforeTimestamp = true) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.log('[Signal Detection] filterDataByTimestamp: No valid data provided');
    return [];
  }

  if (!isValidTimestamp(timestamp)) {
    console.warn('[Signal Detection] filterDataByTimestamp: Invalid timestamp provided:', timestamp);
    return data; // Return original data if timestamp is invalid
  }

  console.log(`[Signal Detection] Filtering ${data.length} data points ${beforeTimestamp ? 'before' : 'after'} timestamp: ${new Date(timestamp).toISOString()}`);

  // Extract timestamps from data
  const dataWithTimestamps = data.map(item => {
    let itemTimestamp = null;
    
    // Try to extract timestamp from different possible properties
    if (item.timestamp && isValidTimestamp(item.timestamp)) {
      itemTimestamp = item.timestamp;
    } else if (item.time && isValidTimestamp(item.time)) {
      itemTimestamp = item.time;
    } else if (item.opentime && isValidTimestamp(item.opentime)) {
      itemTimestamp = item.opentime;
    }
    
    return {
      ...item,
      extractedTimestamp: itemTimestamp
    };
  }).filter(item => item.extractedTimestamp !== null);

  if (dataWithTimestamps.length === 0) {
    console.warn('[Signal Detection] filterDataByTimestamp: No valid timestamps found in data');
    return [];
  }

  // Filter data points by timestamp
  const filteredData = dataWithTimestamps.filter(item => {
    return beforeTimestamp 
      ? item.extractedTimestamp <= timestamp 
      : item.extractedTimestamp >= timestamp;
  });

  console.log(`[Signal Detection] Filtered data: ${filteredData.length} data points`);
  
  if (filteredData.length > 0) {
    console.log(`[Signal Detection] First filtered timestamp: ${new Date(filteredData[0].extractedTimestamp).toISOString()}`);
    console.log(`[Signal Detection] Last filtered timestamp: ${new Date(filteredData[filteredData.length - 1].extractedTimestamp).toISOString()}`);
  }

  return filteredData;
};

/**
 * Calculates the profit or loss from a buy and sell transaction
 * 
 * @param {Object} buyData - Buy transaction data
 * @param {Object} sellData - Sell transaction data
 * @param {number} quantity - Number of shares
 * @returns {Object} - Profit calculation results
 */
const calculateProfit = (buyData, sellData, quantity = 1) => {
  if (!buyData || !sellData) {
    return {
      profit: 0,
      profitPercentage: 0,
      buyPrice: 0,
      sellPrice: 0,
      holdingPeriod: 0,
      success: false,
      error: 'Missing buy or sell data'
    };
  }
  
  // Normalize data to ensure we have the correct format
  const normalizedBuyData = Array.isArray(buyData) ? buyData[0] : buyData;
  const normalizedSellData = Array.isArray(sellData) ? sellData[0] : sellData;
  
  // Extract prices - first check for direct price property in transaction objects
  let buyPrice, sellPrice;
  
  // For buyData, check for price in result object first
  if (normalizedBuyData.result && normalizedBuyData.result.price !== undefined) {
    buyPrice = parseFloat(normalizedBuyData.result.price);
  } else {
    buyPrice = getPriceFromCandle(normalizedBuyData);
  }
  
  // For sellData, check for price in result object first
  if (normalizedSellData.result && normalizedSellData.result.price !== undefined) {
    sellPrice = parseFloat(normalizedSellData.result.price);
  } else {
    sellPrice = getPriceFromCandle(normalizedSellData);
  }
  
  if (buyPrice === null || sellPrice === null) {
    return {
      profit: 0,
      profitPercentage: 0,
      buyPrice: buyPrice || 0,
      sellPrice: sellPrice || 0,
      holdingPeriod: 0,
      success: false,
      error: 'Unable to determine price'
    };
  }
  
  // Calculate profit
  const profit = (sellPrice - buyPrice) * quantity;
  const profitPercentage = (profit / (buyPrice * quantity)) * 100;
  
  // Calculate holding period in milliseconds
  const buyTimestamp = normalizedBuyData.result?.timestamp || normalizedBuyData.timestamp || Date.now();
  const sellTimestamp = normalizedSellData.result?.timestamp || normalizedSellData.timestamp || Date.now();
  const holdingPeriod = sellTimestamp - buyTimestamp;
  
  return {
    profit,
    profitPercentage,
    buyPrice,
    sellPrice,
    buyTimestamp,
    sellTimestamp,
    holdingPeriod,
    holdingPeriodDays: holdingPeriod / (1000 * 60 * 60 * 24),
    success: true
  };
};

/**
 * Get the correct price value from a candle based on specified point type
 * 
 * @param {Object} candle - Candle data with OHLC values
 * @param {string} pointType - Which price to use: open, high, low, close
 * @returns {number|null} - The price value or null if not available
 */
const getPriceFromCandle = (candle, pointType = 'close') => {
  if (!candle) {
    return null;
  }
  
  // Direct price value
  if (typeof candle === 'number') {
    return candle;
  }
  
  // Handle direct price property (added for transaction objects)
  if (candle.price !== undefined) {
    return parseFloat(candle.price);
  }
  
  // Handle simplified data format
  if (candle.value !== undefined) {
    return parseFloat(candle.value);
  }
  
  // Handle OHLC format
  if (candle[pointType] !== undefined) {
    return parseFloat(candle[pointType]);
  }
  
  // Look for price in nested objects
  if (candle.result && typeof candle.result === 'object') {
    // Check for direct price property in result
    if (candle.result.price !== undefined) {
      return parseFloat(candle.result.price);
    }
    
    if (candle.result.value !== undefined) {
      return parseFloat(candle.result.value);
    }
    
    if (candle.result[pointType] !== undefined) {
      return parseFloat(candle.result[pointType]);
    }
  }
  
  // Unable to find price
  return null;
};

/**
 * Detects a crossover (value crossing above threshold)
 * 
 * @param {Array} data - Array of data points
 * @param {number} threshold - Threshold value
 * @param {number} [timestamp=null] - Optional timestamp to filter data
 * @returns {Object|null} - Crossover signal data or null if no crossover
 */
export const detectCrossover = (data, threshold, timestamp = null) => {
  console.log(`[Signal Detection] Detecting crossover above threshold ${threshold}`);
  
  if (!data || !Array.isArray(data) || data.length < 2) {
    console.log(`[Signal Detection] Insufficient data for crossover detection: ${data ? data.length : 'null'} data points`);
    return null;
  }
  
  // Normalize data to ensure consistent format
  const normalizedData = normalizeIndicatorData(data);
  
  // Filter data by timestamp if provided
  let filteredData = normalizedData;
  if (timestamp !== null && isValidTimestamp(timestamp)) {
    console.log(`[Signal Detection] Filtering data before timestamp: ${new Date(timestamp).toISOString()}`);
    filteredData = filterDataByTimestamp(normalizedData, timestamp, true);
    
    if (filteredData.length < 2) {
      console.log(`[Signal Detection] Insufficient filtered data for crossover detection: ${filteredData.length} data points`);
      return null;
    }
  }
  
  // Extract values for comparison
  const values = extractValues(filteredData);
  
  // Log the values for debugging
  console.log(`[Signal Detection] Analyzing ${values.length} values for crossover detection`);
  if (values.length > 0) {
    console.log(`[Signal Detection] First few values: ${values.slice(0, 3).join(', ')}`);
    console.log(`[Signal Detection] Last few values: ${values.slice(-3).join(', ')}`);
  }
  
  // Check for crossover (previous value below threshold, current value above threshold)
  for (let i = 1; i < values.length; i++) {
    const prevValue = values[i - 1];
    const currValue = values[i];
    
    if (prevValue <= threshold && currValue > threshold) {
      console.log(`[Signal Detection] Crossover detected at index ${i}: ${prevValue} -> ${currValue} (threshold: ${threshold})`);
      
      // Get the timestamp for this crossover
      const crossoverTimestamp = filteredData[i].timestamp || 
                                filteredData[i].time || 
                                filteredData[i].opentime || 
                                Date.now();
      
      return {
        index: i,
        previousValue: prevValue,
        currentValue: currValue,
        threshold: threshold,
        timestamp: crossoverTimestamp,
        signalType: 'crossover'
      };
    }
  }
  
  console.log(`[Signal Detection] No crossover detected`);
  return null;
};

/**
 * Detects a crossunder (value crossing below threshold)
 * 
 * @param {Array} data - Array of data points
 * @param {number} threshold - Threshold value
 * @param {number} [timestamp=null] - Optional timestamp to filter data
 * @returns {Object|null} - Crossunder signal data or null if no crossunder
 */
export const detectCrossunder = (data, threshold, timestamp = null) => {
  console.log(`[Signal Detection] Detecting crossunder below threshold ${threshold}`);
  
  if (!data || !Array.isArray(data) || data.length < 2) {
    console.log(`[Signal Detection] Insufficient data for crossunder detection: ${data ? data.length : 'null'} data points`);
    return null;
  }
  
  // Normalize data to ensure consistent format
  const normalizedData = normalizeIndicatorData(data);
  
  // Filter data by timestamp if provided
  let filteredData = normalizedData;
  if (timestamp !== null && isValidTimestamp(timestamp)) {
    console.log(`[Signal Detection] Filtering data before timestamp: ${new Date(timestamp).toISOString()}`);
    filteredData = filterDataByTimestamp(normalizedData, timestamp, true);
    
    if (filteredData.length < 2) {
      console.log(`[Signal Detection] Insufficient filtered data for crossunder detection: ${filteredData.length} data points`);
      return null;
    }
  }
  
  // Extract values for comparison
  const values = extractValues(filteredData);
  
  // Log the values for debugging
  console.log(`[Signal Detection] Analyzing ${values.length} values for crossunder detection`);
  if (values.length > 0) {
    console.log(`[Signal Detection] First few values: ${values.slice(0, 3).join(', ')}`);
    console.log(`[Signal Detection] Last few values: ${values.slice(-3).join(', ')}`);
  }
  
  // Check for crossunder (previous value above threshold, current value below threshold)
  for (let i = 1; i < values.length; i++) {
    const prevValue = values[i - 1];
    const currValue = values[i];
    
    if (prevValue >= threshold && currValue < threshold) {
      console.log(`[Signal Detection] Crossunder detected at index ${i}: ${prevValue} -> ${currValue} (threshold: ${threshold})`);
      
      // Get the timestamp for this crossunder
      const crossunderTimestamp = filteredData[i].timestamp || 
                                 filteredData[i].time || 
                                 filteredData[i].opentime || 
                                 Date.now();
      
      return {
        index: i,
        previousValue: prevValue,
        currentValue: currValue,
        threshold: threshold,
        timestamp: crossunderTimestamp,
        signalType: 'crossunder'
      };
    }
  }
  
  console.log(`[Signal Detection] No crossunder detected`);
  return null;
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use the SignalTracker class methods directly
 */
const updateLastValue = (indicatorType, threshold, operator, value) => {
  console.warn('[Signal Detection] updateLastValue is deprecated. Use SignalTracker.trackValue instead.');
  defaultTracker.trackValue(indicatorType, threshold, operator, value);
};

export {
  detectSignals,
  calculateProfit,
  getPriceFromCandle,
  updateLastValue,
  IndicatorAdapterFactory
};
